// ImageViewer.jsx - Minimal Professional Image Viewer
import { useState, useEffect, useRef } from "react";
import "./ImageViewer.css";
import apiClient from "api/axios";

const ImageViewer = ({
  images = [],
  initialIndex = 0,
  onClose,
  isOpen = false,
  title = "Image Viewer",
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [showControls, setShowControls] = useState(true)
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const imageRef = useRef(null)

  const hasMultipleImages = images.length > 1

  // Sample images if none provided
  const defaultImages = [
    "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1200",
    "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1200",
    "https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200",
    "https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=1200",
  ]

  const imageList = images.length > 0 ? images : defaultImages

  const handlePrev = e => {
    e?.stopPropagation()
    setCurrentIndex(prev => (prev === 0 ? imageList.length - 1 : prev - 1))
    setZoomLevel(1)
  }

  const handleNext = e => {
    e?.stopPropagation()
    setCurrentIndex(prev => (prev === imageList.length - 1 ? 0 : prev + 1))
    setZoomLevel(1)
  }

  const handleThumbnailClick = (index, e) => {
    e?.stopPropagation()
    setCurrentIndex(index)
    setZoomLevel(1)
  }

  const handleZoomIn = e => {
    e?.stopPropagation()
    setZoomLevel(prev => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = e => {
    e?.stopPropagation()
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5))
  }

  const resetZoom = e => {
    e?.stopPropagation()
    setZoomLevel(1)
  }

  const toggleControls = () => {
    setShowControls(!showControls)
  }

  const handleKeyDown = e => {
    if (!isOpen) return

    switch (e.key) {
      case "ArrowLeft":
        if (hasMultipleImages) handlePrev()
        break
      case "ArrowRight":
        if (hasMultipleImages) handleNext()
        break
      case "Escape":
        onClose()
        break
      case "+":
      case "=":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
          handleZoomIn(e)
        }
        break
      case "-":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
          handleZoomOut(e)
        }
        break
      case "0":
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
          resetZoom(e)
        }
        break
      case " ":
        e.preventDefault()
        toggleControls()
        break
      default:
        break
    }
  }

  // Touch handlers for mobile swipe
  const handleTouchStart = e => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = e => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || !hasMultipleImages) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      handleNext()
    } else if (isRightSwipe) {
      handlePrev()
    }

    setTouchStart(null)
    setTouchEnd(null)
  }

  const handleImageClick = e => {
    if (e.target === imageRef.current) {
      toggleControls()
    }
  }

  const handleDownload = async e => {
    e.stopPropagation()

    const imageUrl = imageList[currentIndex]

    try {
      // Use your API client with responseType as 'blob'
      const response = await apiClient.get(imageUrl, {
        responseType: "blob",
        skipToast: true, // Prevent toast on error
      })

      if (response.data) {
        const blob = response.data
        const blobUrl = URL.createObjectURL(blob)

        // Get filename from response headers or generate one
        const contentDisposition = response.headers["content-disposition"]
        let filename = `image-${currentIndex + 1}.${getExtensionFromBlob(blob)}`

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
          }
        }

        const link = document.createElement("a")
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100)

        return true
      }
      throw new Error("No data received")
    } catch (error) {
      console.log(error);
      
      // Fallback: Open in new tab
      window.open(imageUrl, "_blank")
    }
  }

  const getExtensionFromBlob = blob => {
    const mime = blob.type
    const extensions = {
      "image/jpeg": "jpg",
      "image/jpg": "jpg",
      "image/png": "png",
      "image/gif": "gif",
      "image/webp": "webp",
      "image/svg+xml": "svg",
    }
    return extensions[mime] || "jpg"
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.body.style.overflow = "auto"
    }
  }, [isOpen, currentIndex, hasMultipleImages])

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex)
      setShowControls(true)
    }
  }, [isOpen, initialIndex])

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowControls(false)
    }, 3000)

    return () => clearTimeout(timer)
  }, [currentIndex, zoomLevel])

  if (!isOpen) return null

  const currentImage = imageList[currentIndex]

  return (
    <div
      className="image-viewer-modal"
      onClick={onClose}
      data-multiple={hasMultipleImages}
    >
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Top Controls Bar */}
        <div
          className={`controls-bar top-bar ${
            showControls ? "visible" : "hidden"
          }`}
        >
          <div className="controls-left">
            <span className="image-counter">
              {currentIndex + 1} / {imageList.length}
            </span>
          </div>

          <div className="controls-center">
            <h5 className="viewer-title">{title}</h5>
          </div>

          <div className="controls-right">
            <button
              className="icon-btn"
              onClick={handleDownload}
              title="Download"
            >
              <i className="bx bx-download"></i>
            </button>
            <button
              className="icon-btn close-btn"
              onClick={onClose}
              title="Close (ESC)"
            >
              <i className="bx bx-x"></i>
            </button>
          </div>
        </div>

        {/* Main Image Container */}
        <div
          className="image-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleImageClick}
        >
          <img
            ref={imageRef}
            src={currentImage}
            alt={`Image ${currentIndex + 1}`}
            className={`main-image ${zoomLevel !== 1 ? "zoomable" : ""}`}
            style={{ transform: `scale(${zoomLevel})` }}
            draggable="false"
          />

          {/* Navigation Arrows (Only for multiple images) */}
          {hasMultipleImages && (
            <>
              <button
                className="nav-arrow prev-arrow"
                onClick={handlePrev}
                aria-label="Previous image"
              >
                <i className="bx bx-chevron-left"></i>
              </button>
              <button
                className="nav-arrow next-arrow"
                onClick={handleNext}
                aria-label="Next image"
              >
                <i className="bx bx-chevron-right"></i>
              </button>
            </>
          )}

          {/* Zoom Level Indicator */}
          <div
            className={`zoom-indicator ${
              zoomLevel !== 1 || !showControls ? "visible" : "hidden"
            }`}
          >
            {Math.round(zoomLevel * 100)}%
          </div>
        </div>

        {/* Bottom Controls Bar */}
        <div
          className={`controls-bar bottom-bar ${
            showControls ? "visible" : "hidden"
          }`}
        >
          <div className="controls-left">
            {/* Zoom Controls */}
            <div className="zoom-controls">
              <button
                className="icon-btn zoom-btn"
                onClick={handleZoomOut}
                title="Zoom Out (Ctrl -)"
              >
                <i className="bx bx-zoom-out"></i>
              </button>

              <button
                className="icon-btn reset-btn"
                onClick={resetZoom}
                title="Reset Zoom (Ctrl 0)"
              >
                <i className="bx bx-reset"></i>
              </button>

              <button
                className="icon-btn zoom-btn"
                onClick={handleZoomIn}
                title="Zoom In (Ctrl +)"
              >
                <i className="bx bx-zoom-in"></i>
              </button>
            </div>
          </div>

          <div className="controls-center">
            {/* Navigation Controls (Only for multiple images) */}
            {hasMultipleImages && (
              <div className="nav-controls">
                <button className="icon-btn nav-btn" onClick={handlePrev}>
                  <i className="bx bx-skip-previous"></i>
                </button>
                <span className="nav-text">
                  {currentIndex + 1} of {imageList.length}
                </span>
                <button className="icon-btn nav-btn" onClick={handleNext}>
                  <i className="bx bx-skip-next"></i>
                </button>
              </div>
            )}
          </div>

          <div className="controls-right">
            <button
              className="icon-btn"
              onClick={toggleControls}
              title="Toggle Controls (Space)"
            >
              <i className={`bx ${showControls ? "bx-hide" : "bx-show"}`}></i>
            </button>
          </div>
        </div>

        {/* Thumbnails (Only for multiple images) */}
        {hasMultipleImages && (
          <div
            className={`thumbnails-bar ${showControls ? "visible" : "hidden"}`}
          >
            <div className="thumbnails-scroll">
              {imageList.map((image, index) => (
                <div
                  key={index}
                  className={`thumbnail-item ${
                    index === currentIndex ? "active" : ""
                  }`}
                  onClick={e => handleThumbnailClick(index, e)}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="thumbnail-img"
                    loading="lazy"
                  />
                  {index === currentIndex && (
                    <div className="thumbnail-active-indicator">
                      <i className="bx bx-check"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tap Hint for Mobile */}
      <div className="tap-hint">Tap image to show/hide controls</div>
    </div>
  )
}

export default ImageViewer
